import { useState } from 'react';
import { Alert, Box, Button, Skeleton, Stack } from '@mui/material';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { MAX_WHITELABEL_HOSTS } from '@/api/saasAuthReseller';
import type { ApiError } from '@/api/errors';
import { useCanWrite } from '@/auth/authHooks';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { useActionError } from '@/components/useActionError';
import { describeApiError } from '@/components/useApiErrorHandler';
import { appHostOf, brandHref, recordsForHelper, stepFor } from './domainState';
import { DomainForm } from './DomainForm';
import { DomainLiveCard } from './DomainLiveCard';
import { DomainStep } from './DomainStep';
import { ProviderStep } from './ProviderStep';
import { RecordsTable } from './RecordsTable';
import { useDomainHelp } from './useDomainHelp';
import { TOO_MANY_HOSTS, useWhitelabelDomain } from './useWhitelabelDomain';

function domainErrorMessage(err: ApiError | null, t: TFunction): string | null {
  if (!err) return null;
  if (err.details?.code === TOO_MANY_HOSTS) {
    return t('domain.tooManyHosts', { max: MAX_WHITELABEL_HOSTS });
  }
  return describeApiError(err, t).message;
}

export function DomainScreen() {
  const { t } = useTranslation();
  const describe = useActionError();
  const canWrite = useCanWrite();
  const domainState = useWhitelabelDomain();
  const helpState = useDomainHelp();
  const [confirmChange, setConfirmChange] = useState(false);
  const canEdit = canWrite && domainState.status !== 'forbidden';
  const step = stepFor(domainState.dto);
  const dto = domainState.dto;
  const loadError = domainErrorMessage(domainState.error, t);
  const mutationError = domainErrorMessage(domainState.mutationError, t);
  const helpError = domainErrorMessage(helpState.error, t);
  const href = brandHref(dto);

  return (
    <Box>
      <PageHeader id="domain" />
      {!canEdit ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('domain.readOnly')}
        </Alert>
      ) : null}
      {domainState.status === 'loading' ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={180} />
        </Box>
      ) : null}
      {domainState.status === 'error' ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={domainState.reload}>
              {t('common.retry')}
            </Button>
          }
        >
          {loadError ?? t('common.loadFailed')}
        </Alert>
      ) : null}
      {domainState.status === 'ready' || domainState.status === 'forbidden' ? (
        <Stack spacing={2}>
          {/* While the change confirm is open, its own error is shown inside it — not twice. */}
          <ActionErrorAlert
            error={
              mutationError && !confirmChange
                ? { message: mutationError, requestId: domainState.mutationError?.requestId }
                : null
            }
          />
          {helpError && !helpState.exhausted ? <Alert severity="error">{helpError}</Alert> : null}
          <DomainStep
            n={1}
            title={t('domain.step1.title')}
            subtitle={
              dto.configured && dto.domain ? dto.domain : t('domain.step1.subtitle')
            }
            open={step === 'domain'}
            done={step !== 'domain'}
            disabled={!canEdit}
            onChange={() => setConfirmChange(true)}
          >
            <DomainForm
              onSave={async (domain, prefix) => {
                try {
                  await domainState.save(domain, prefix);
                } catch (err) {
                  describe(err);
                  throw err;
                }
              }}
              disabled={!canEdit}
              saving={domainState.saving}
            />
          </DomainStep>
          <DomainStep
            n={2}
            title={t('domain.step2.title')}
            subtitle={t('domain.step2.subtitle')}
            open={step === 'records'}
            done={step === 'live'}
          >
            <ProviderStep
              help={helpState.help}
              questionsLeft={helpState.questionsLeft}
              exhausted={helpState.exhausted}
              askFailure={helpState.askFailure}
              aiUnavailable={helpState.aiUnavailable}
              starting={helpState.starting}
              asking={helpState.asking}
              disabled={!canEdit}
              onStart={(provider) =>
                helpState.start(dto.domain ?? '', recordsForHelper(dto), provider)
              }
              onAsk={helpState.ask}
            />
          </DomainStep>
          <DomainStep
            n={3}
            title={t('domain.step3.title')}
            subtitle={t('domain.step3.subtitle')}
            open={step === 'records'}
            done={step === 'live'}
          >
            <RecordsTable
              records={dto.dnsRecords}
              disabled={!canEdit}
              verifying={domainState.verifying}
              onVerify={async () => {
                try {
                  await domainState.verify();
                } catch (err) {
                  describe(err);
                  throw err;
                }
              }}
            />
          </DomainStep>
          {step === 'live' && dto.domain ? (
            <DomainLiveCard
              domain={dto.domain}
              appHost={appHostOf(dto)}
              brandHref={href}
              disabled={!canEdit}
              verifying={domainState.verifying}
              onVerify={async () => {
                try {
                  await domainState.verify();
                } catch (err) {
                  describe(err);
                  throw err;
                }
              }}
            />
          ) : null}
        </Stack>
      ) : null}

      <ConfirmDialog
        open={confirmChange}
        title={
          dto.domain
            ? t('domain.changeConfirmTitleNamed', { host: dto.domain })
            : t('domain.changeConfirmTitle')
        }
        consequence={t('domain.changeConfirmBody')}
        confirmLabel={t('domain.changeConfirm')}
        onConfirm={domainState.remove}
        destructive
        onCancel={() => setConfirmChange(false)}
        errorMessage={(err) => describe(err).message}
      />
    </Box>
  );
}
